import './index.scss';
import { Card, Switch, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { set, cloneDeep, get } from 'lodash';
import { IoShieldCheckmark } from '@react-icons/all-files/io5/IoShieldCheckmark';
import { DEAL_SUBMISSION_MATCH_STATUS_TITLES } from '@zorba-shared/core/Constants/offer';
import { MARKETPLACE_OPTIONS } from '@zorba-shared/core/Constants/whitelabelMarketplace';
import SaveChangesDrawer from '../../../../components/forms/SaveChangesDrawer';
import ZorbaInput from '../../../../components/forms/ZorbaInput';
import PhoneInput from '../../../../components/forms/PhoneInput';
import { diffObject } from '../../../../services/validationService';
import { updateBuyerDetails } from '../../../../reducer/Buyer/actions';
import { postRequest } from '../../../../services/requestService';
import { ZorbaSearchSelect, ZorbaSelect } from '../../../../components/forms';
import { USER_KIND_OPTIONS, PERMISSION_TITLES } from '../../../../constants/permission';
import { getAllOrganizationPowerBuyers, getAllAgents } from '../../../../reducer/User/actions';
import {
  PERMISSION_SET_ADMIN,
  PERMISSION_SET_BUYER_AGENT,
  PERMISSION_SET_BUYER_SUPERUSER,
  PERMISSION_SET_SELLER_AGENT,
} from '@zorba-shared/core/Constants/auth';

export default (props) => {
  const {
    selectedBuyer,
    changedParams,
    setChangedParams,
    params,
    setParams,
    revertChangesHandler,
  } = props;
  const { userDetails } = useSelector(({ AuthReducer }) => AuthReducer);
  const [selectedOrganization, setSelectedOrganization] = useState([]);
  const { organizationPowerBuyers, agents } = useSelector(
    ({ UserReducer }) => UserReducer,
  );
  const organization = get(selectedBuyer, 'defaultOrganization');
  const isMarketplaceUser = organization?.marketplacePartner;
  const dispatch = useDispatch();
  const changedPropertiesCount = Object.keys(changedParams).length;
  const isSaveChangesVisible = changedPropertiesCount > 0;

  useEffect(() => {
    dispatch(getAllOrganizationPowerBuyers());
    dispatch(getAllAgents()); // Fetch all agents (both buyer_agent and seller_agent)
  }, []);

  const onChange = (key, value) => {
    const updatedParams = set({ ...params }, key, value);
    const updatedChangedParams = diffObject(updatedParams, selectedBuyer);
    
    setParams({ ...updatedParams });
    setChangedParams({ ...updatedChangedParams });
  };

  const getRequestPayload = (updatedParams) => {
    const requestPayload = {};

    Object.keys(updatedParams).forEach((paramKey) => {
      if (paramKey === 'defaultOrganization') {
        const {
          name,
          isPowerBuyer,
          buyerConfig,
          globalNotificationsRecipients,
          isSoftPaused,
          dataSubscription,
          shouldAutoSubmitProperties,
          defaultFeePercentage,
          buyersAppFeatureFlags,
          defaultLowBallTreshold,
          marketplacePartner,
          managedMarketplace,
        } = updatedParams[paramKey];
        requestPayload.organizationName = name;
        requestPayload.isPowerBuyer = isPowerBuyer;
        requestPayload.marketplacePartner = marketplacePartner;
        requestPayload.isSoftPaused = isSoftPaused;
        requestPayload.shouldAutoSubmitProperties = shouldAutoSubmitProperties;
        requestPayload.dataSubscription = dataSubscription;
        requestPayload.buyerConfig = buyerConfig;
        requestPayload.globalNotificationsRecipients =
          globalNotificationsRecipients;
        requestPayload.defaultFeePercentage = defaultFeePercentage;
        requestPayload.defaultLowBallTreshold = defaultLowBallTreshold;
        requestPayload.buyersAppFeatureFlags = buyersAppFeatureFlags;
        requestPayload.managedMarketplace = managedMarketplace;
      } else {
        requestPayload[paramKey] = updatedParams[paramKey];
      }
    });

    return requestPayload;
  };

  const onSubmit = () => {
    if (Object.values(changedParams).length === 0) return;
    const requestPayload = getRequestPayload(changedParams);
    dispatch(
      updateBuyerDetails({
        ...requestPayload,
        requestedUserId: params._id,
      }),
    );
  };

  const notificationsRecipientsOptions = organization.members.map((member) => ({
    text: member.email,
    value: member.email,
  }));

  const PERMISSION_OPTIONS = [
    { value: PERMISSION_SET_ADMIN, text: PERMISSION_TITLES.admin },
    { value: PERMISSION_SET_BUYER_SUPERUSER, text: PERMISSION_TITLES.superuser },
    { value: PERMISSION_SET_BUYER_AGENT, text: PERMISSION_TITLES.buyer_agent },
    { value: PERMISSION_SET_SELLER_AGENT, text: PERMISSION_TITLES.seller_agent },
  ];

  const tranformOrganizationItem = (orgList) =>
    orgList.map((org) => ({
      value: org._id,
      title: org.members[0]?.name
        ? `- ${org.members[0].name} (${org.members[0].email})`
        : '',
      label: (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>{`${org.name} ${org.members[0]?.name
            ? `- ${org.members[0].name} (${org.members[0].email})`
            : ''
            }`}</span>
          {org?.verification?.verified && (
            <Tooltip title="Verified organization">
              <span>
                <IoShieldCheckmark className="text-success" />
              </span>
            </Tooltip>
          )}
        </div>
      ),
    }));

  const fetchOrganizations = async (searchText) => {
    const query = searchText.trim();
    const res = await postRequest('/backoffice/organization/search', {
      query,
    });
    const { items } = res.data;

    return tranformOrganizationItem(items);
  };

  /**
   * An helper function designed to build a ZorbaSearchSelect value object
   */
  const setDefaultOrganizationValue = (label, value) => {
    let orgSelectOption = [];
    if (label && value) {
      orgSelectOption = [{ label, value, title: label }];
    }
    setSelectedOrganization(orgSelectOption);
  };

  const onChangeOrganization = (options) => {
    const org = options.length > 1 ? options.pop() : options[0];
    const { value, title } = org || {};

    if (value) onChange('defaultOrganizationId', value);
    setDefaultOrganizationValue(title, value);
  };

  const onChangeDefaulFee = (e) => {
    onChange('defaultOrganization.defaultFeePercentage', e.target.value / 100);
  };

  const onChangeLowBallThreshold = (e) => {
    onChange(
      'defaultOrganization.defaultLowBallTreshold',
      e.target.value / 100,
    );
  };

  useEffect(() => {
    if (selectedBuyer) {
      const buyerObj = cloneDeep(selectedBuyer);
      setParams(buyerObj);
      setChangedParams({});
    }
  }, [selectedBuyer]);

  /**
   * Watch defaultOrganization and set selectedValue of DefaultOrganization
   */
  useEffect(() => {
    if (params.defaultOrganization) {
      setDefaultOrganizationValue(
        params.defaultOrganization.name,
        params.defaultOrganization._id,
      );
    }
  }, [params.defaultOrganization]);

  const isUserAllowedToChangeOrganization = [
    'kobi@getzorba.com',
    'or@getzorba.com',
    'mateusz.kwasny@getzorba.com',
  ].includes(userDetails.email);

  // Check if user has agent permissions
  const hasAgentPermissions = true 
  // || params.permissions?.some(permission => 
  //   permission === PERMISSION_SET_BUYER_AGENT || permission === PERMISSION_SET_SELLER_AGENT
  // );

  // Find the selected agent to get their hubspotAgentId and slackUserId
  const selectedAgent = agents.find(agent => agent.value === params.associatedAgent);
  const selectedAgentHubspotId = selectedAgent?.hubspotAgentId;
  const selectedAgentSlackUserId = selectedAgent?.slackUserId;

  if (!selectedBuyer) return null;
  return (
    <>
      <div className="buyer-info-tab">
        <Card title="User Settings" style={{ backgroundColor: 'rgb(253, 251, 251)', marginBottom: '25px' }}>
          <div className="row">
            <div className="form-item">
              <ZorbaInput
                label="First name"
                onChange={(e) => onChange('firstName', e.target.value)}
                value={params.firstName}
              />
            </div>

            <div className="form-item">
              <ZorbaInput
                label="Last name"
                onChange={(e) => onChange('lastName', e.target.value)}
                value={params.lastName}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-item">
              <ZorbaInput
                label="Email"
                onChange={(e) => onChange('email', e.target.value)}
                value={params.email}
              />
            </div>
            <div className="form-item">
              <PhoneInput
                fullWidth
                label="Phone Number"
                defaultValue={params.phoneNumber?.number}
                value={params.phoneNumber}
                placeholder="Phone number"
                onChange={(value) => onChange('phoneNumber', value)}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-item">
              <ZorbaSelect
                label="Select user type(s)"
                placeholder="Agent, Buyer, Investor"
                mode="multiple"
                onChange={(e) => onChange('kind', e)}
                value={params.kind}
                options={USER_KIND_OPTIONS}
              />
            </div>
            <div className="form-item">
              <ZorbaSelect
                label="User Permissions"
                placeholder="Select permissions"
                mode="multiple"
                onChange={(e) => onChange('permissions', e)}
                value={params.permissions}
                options={PERMISSION_OPTIONS}
              />
            </div>
          </div>
          {hasAgentPermissions && (
            <div className="row">
                <div className="form-item">
                  <ZorbaInput
                    label="HubSpot Agent ID"
                    value={params.hubspotAgentId}
                    onChange={(e) => onChange('hubspotAgentId', e.target.value)}
                    placeholder="HubSpot Agent ID from selected agent"
                  />
                </div>
                <div className="form-item">
                  <ZorbaInput
                    label="Slack User ID"
                    value={params.slackUserId}
                    onChange={(e) => onChange('slackUserId', e.target.value)}
                    placeholder="Slack User ID from selected agent"
                  />
                </div>
              {!selectedAgentHubspotId && !selectedAgentSlackUserId && (
                <div className="form-item">
                  {/* Empty div to maintain layout balance */}
                </div>
              )}
            </div>
          )}
          <div className="row">
            <div className="form-item">
              <ZorbaSelect
                label="Associated Seller Agent"
                onChange={(e) => onChange('associatedAgent', e)}
                value={params.associatedAgent}
                options={agents}
              />
            </div>
          </div>
          <div className="row">
            <div className="form-item">
              <ZorbaSelect
                label="Associated Buyer Agent"
                onChange={(e) => onChange('associatedBuyerAgent', e)}
                value={params.associatedBuyerAgent}
                options={agents}
              />
            </div>
            <div className="form-item">
              {/* Empty div to maintain layout balance */}
            </div>
          </div>
          {isUserAllowedToChangeOrganization && (
            <ZorbaSearchSelect
              label="Select Company"
              showSearch
              mode="multiple"
              placeholder="Search Organization"
              fetchOptions={fetchOrganizations}
              onChange={onChangeOrganization}
              value={selectedOrganization}
              optionLabelProp="label"
              maxTagCount={1}
            />
          )}
          <div className="row">
            <div className="form-item">
              <ZorbaInput
                label="Marketplace Admin Account (e.g: malve-capital)"
                onChange={(e) => onChange('defaultOrganization.managedMarketplace', e.target.value)}
                value={params.defaultOrganization?.managedMarketplace}
              />
            </div>
            <div className="form-item">
              <ZorbaSelect
                label="Associated Marketplace (as a member)"
                onChange={(e) =>
                  onChange('defaultOrganization.marketplacePartner', e)
                }
                value={params.defaultOrganization?.marketplacePartner}
                options={MARKETPLACE_OPTIONS}
              />
            </div>
          </div>
          <ZorbaSelect
            label="Excluded funds from matching algorithm"
            mode="multiple"
            onChange={(e) => onChange('excludedOrganizationsFromMatching', e)}
            options={organizationPowerBuyers}
            value={params.excludedOrganizationsFromMatching}
          />
          <ZorbaSelect
            label="Default filters for the Buyers App"
            mode="multiple"
            onChange={(e) => onChange('buyersAppDefaultFilters', e)}
            options={Object.keys(DEAL_SUBMISSION_MATCH_STATUS_TITLES).map(
              (key) => ({
                text: DEAL_SUBMISSION_MATCH_STATUS_TITLES[key],
                value: key,
              }),
            )}
            value={params.buyersAppDefaultFilters}
          />
          <div className="row column">
            <div className="form-item form-item--switch">
              <Switch
                checked={params.isBanned}
                onChange={(checked) => onChange('isBanned', checked)}
              />
              <label>Is banned?</label>
            </div>
            <span className="global-notifications-hint">
              If active, the seller won't be able to use the partners app
            </span>
          </div>
          <div className="row column">
            <div className="form-item form-item--switch">
              <Switch
                checked={!params.unsubscribed?.email}
                onChange={(checked) => onChange('unsubscribed.email', !checked)}
              />
              <label>Subscribed to email notificiations</label>
            </div>
          </div>
        </Card>
        <Card title="Organization level settings" style={{ backgroundColor: 'rgb(253, 251, 251)', marginTop: '25px' }}>
          <div className="row">
            <div className="form-item">
              <ZorbaInput
                label="Company name"
                onChange={(e) =>
                  onChange('defaultOrganization.name', e.target.value)
                }
                value={params.defaultOrganization?.name}
              />
            </div>
            <div className="form-item">
              {/* Empty div to maintain layout balance */}
            </div>
          </div>
          <ZorbaSelect
            label="Global notifications recipients"
            mode="tags"
            onChange={(e) =>
              onChange('defaultOrganization.globalNotificationsRecipients', e)
            }
            value={params.defaultOrganization?.globalNotificationsRecipients}
            options={notificationsRecipientsOptions}
            className="test"
          />
          <span className="global-notifications-hint">
            The email addresses you enter here will receive every single deal,
            in addition to the members of your company or the recipients
            specified in the buy box recipients feature.
          </span>
          <ZorbaSelect
            label="Do we buy with foundation issues?"
            onChange={(e) =>
              onChange(
                'defaultOrganization.buyerConfig.isBuyingWithFoundationIssues',
                e,
              )
            }
            options={[
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ]}
            value={
              params.defaultOrganization?.buyerConfig
                ?.isBuyingWithFoundationIssues
            }
          />
          <ZorbaSelect
            label="Do we buy MLS deals?"
            onChange={(e) =>
              onChange('defaultOrganization.buyerConfig.allowMlsDeals', e)
            }
            options={[
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ]}
            value={params.defaultOrganization?.buyerConfig?.allowMlsDeals}
          />
          <div className="row">
            <div className="form-item">
              <ZorbaInput
                className="default-fee-percentage-input"
                fullWidth
                label="Default fee percentage"
                symbol=""
                addonAfter="%"
                onChange={onChangeDefaulFee}
                value={
                  params.defaultOrganization?.defaultFeePercentage &&
                    !Number.isNaN(params.defaultOrganization?.defaultFeePercentage)
                    ? Math.round(
                      params.defaultOrganization.defaultFeePercentage * 100,
                    )
                    : 0
                }
              />
            </div>
            <div className="form-item">
              <ZorbaInput
                fullWidth
                label="Low-ball percentage threshold"
                symbol=""
                addonAfter="%"
                onChange={onChangeLowBallThreshold}
                value={
                  params.defaultOrganization?.defaultLowBallTreshold &&
                    !Number.isNaN(params.defaultOrganization?.defaultLowBallTreshold)
                    ? Math.round(
                      params.defaultOrganization.defaultLowBallTreshold * 100,
                    )
                    : ''
                }
              />
            </div>
          </div>
          <div className="row column">
            <div className="form-item form-item--switch">
              <Switch
                checked={params.defaultOrganization?.isPowerBuyer}
                onChange={(checked) =>
                  onChange('defaultOrganization.isPowerBuyer', checked)
                }
              />
              <label>Is organization power buyer?</label>
            </div>
            <span className="global-notifications-hint">
              If active, the buyer’s buy box will be added to the shared buy box
              of our active buyers
            </span>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.buyerConfig?.doesIncludeFeeInOffer
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.doesIncludeFeeInOffer',
                    checked,
                  )
                }
              />
              <label>Include Zorba fee in our offer?</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  !params.defaultOrganization?.buyerConfig
                    ?.unsubscribeFromEodEmail
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.unsubscribeFromEodEmail',
                    !checked,
                  )
                }
              />
              <label>Subscribed to end-of-day report</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  !params.defaultOrganization?.buyerConfig
                    ?.unsubscribeFromNewDealsEmail
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.unsubscribeFromNewDealsEmail',
                    !checked,
                  )
                }
              />
              <label>Subscribed to new deal email notifications</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  !params.defaultOrganization?.buyerConfig
                    ?.unsubscribeFromOfferUpdatesEmail
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.unsubscribeFromOfferUpdatesEmail',
                    !checked,
                  )
                }
              />
              <label>
                Subscribed to offer email update (Thank you for submitting an
                offer email)
              </label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  !params.defaultOrganization?.buyerConfig
                    ?.unsubscribeFromOfferFollowUps
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.unsubscribeFromOfferFollowUps',
                    !checked,
                  )
                }
              />
              <label>Subscribed to offer email follow-ups</label>
            </div>
          </div>
          {isMarketplaceUser && (
            <>
              <div className="row">
                <div className="form-item form-item--switch">
                  <Switch
                    checked={
                      !params.defaultOrganization?.buyerConfig
                        ?.unsubscribeFromNewDealsSMS
                    }
                    onChange={(checked) =>
                      onChange(
                        'defaultOrganization.buyerConfig.unsubscribeFromNewDealsSMS',
                        !checked,
                      )
                    }
                  />
                  <label>Subscribed to new deal SMS notifications</label>
                </div>
              </div>
              <div className="row">
                <div className="form-item form-item--switch">
                  <Switch
                    checked={
                      !params.defaultOrganization?.buyerConfig
                        ?.unsubscribeFromOfferUpdatesSMS
                    }
                    onChange={(checked) =>
                      onChange(
                        'defaultOrganization.buyerConfig.unsubscribeFromOfferUpdatesSMS',
                        !checked,
                      )
                    }
                  />
                  <label>Subscribed to offer SMS follow-ups</label>
                </div>
              </div>
            </>
          )}
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.buyerConfig?.requireDoubleClose
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyerConfig.requireDoubleClose',
                    checked,
                  )
                }
              />
              <label>The buyer requires the wholesaler to double close</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={params.defaultOrganization?.isSoftPaused}
                onChange={(checked) =>
                  onChange('defaultOrganization.isSoftPaused', checked)
                }
              />
              <label>Is soft paused?</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={params.defaultOrganization?.shouldAutoSubmitProperties}
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.shouldAutoSubmitProperties',
                    checked,
                  )
                }
              />
              <label>
                {`Should we auto-submit properties submitted through the partner's
              app to all buyers?`}
              </label>
            </div>
          </div>
          <h3>Data app feature flags:</h3>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.dataSubscription?.featureFlags
                    ?.transactionsReport?.isActive
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.dataSubscription.featureFlags.transactionsReport.isActive',
                    checked,
                  )
                }
              />
              <label>Transactions report</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.dataSubscription?.featureFlags
                    ?.marketOverviewReport?.isActive
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.dataSubscription.featureFlags.marketOverviewReport.isActive',
                    checked,
                  )
                }
              />
              <label>Market overview report</label>
            </div>
          </div>
          <h3>Buyers app feature flags:</h3>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.buyersAppFeatureFlags?.preDeals
                    ?.isActive
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyersAppFeatureFlags.preDeals.isActive',
                    checked,
                  )
                }
              />
              <label>Pre Deals</label>
            </div>
          </div>
          <div className="row">
            <div className="form-item form-item--switch">
              <Switch
                checked={
                  params.defaultOrganization?.buyersAppFeatureFlags
                    ?.missedOpportunities?.isActive
                }
                onChange={(checked) =>
                  onChange(
                    'defaultOrganization.buyersAppFeatureFlags.missedOpportunities.isActive',
                    checked,
                  )
                }
              />
              <label>Missed opportunities report</label>
            </div>
          </div>
        </Card>
      </div>
      <SaveChangesDrawer
        visible={isSaveChangesVisible}
        saveLoading={false}
        text={
          changedPropertiesCount === 1
            ? `You've changed 1 property`
            : `You've changed ${changedPropertiesCount} properties`
        }
        onSave={onSubmit}
        onRevert={revertChangesHandler}
      />
    </>
  );
};
